import { logError } from "@/logging/logging";
import { GUID, robustFetchJson } from "@faro-lotv/foundation";
import {
  IElementOdometryPath,
  IQuat,
  IVec3,
  validateQuat,
  validateVec3,
} from "@faro-lotv/ielement-types";
import { createSignedUrlForIElementUri } from "@faro-lotv/project-source";
import { ProjectApi, useApiClientContext } from "@faro-lotv/service-wires";
import { useCallback, useSyncExternalStore } from "react";

/** A single sensor pose sampled along a trajectory, in the local space of its odometry path */
export type OdometryPose = {
  /** Capture time, as a unix timestamp in seconds */
  timestamp: string;

  /** Position of the sensor */
  pos: IVec3;

  /** Orientation of the sensor */
  rot: IQuat;
};

/** How long a trajectory is kept in memory after the last component using it unmounted */
const ODOMETRY_PATH_LIFESPAN = 600000;

type OdometryPathEntry = {
  /** The key identifying this entry in the cache */
  key: GUID;

  /** The promise loading the trajectory */
  promise: Promise<OdometryPose[]>;

  /** The loaded trajectory, once the promise resolved */
  poses?: OdometryPose[];

  /** The components currently interested in this trajectory */
  listeners: Set<() => void>;

  /** The timeout evicting this entry, started when the last listener unsubscribed */
  disposeTimeout?: number;
};

const CACHE = new Map<GUID, OdometryPathEntry>();

function scheduleEvictionIfUnused(entry: OdometryPathEntry): void {
  if (entry.listeners.size > 0 || entry.disposeTimeout !== undefined) return;
  entry.disposeTimeout = window.setTimeout(() => {
    CACHE.delete(entry.key);
  }, ODOMETRY_PATH_LIFESPAN);
}

/**
 * @param obj to check
 * @returns true if the object is a valid OdometryPose
 */
function isOdometryPose(obj: unknown): obj is OdometryPose {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "timestamp" in obj &&
    typeof obj.timestamp === "string" &&
    "pos" in obj &&
    validateVec3(obj.pos) &&
    "rot" in obj &&
    validateQuat(obj.rot)
  );
}

/**
 * @param obj to check
 * @returns true if the object is the list of poses expected in an odometry path file
 */
export function isOdometryPoseArray(obj: unknown): obj is OdometryPose[] {
  return Array.isArray(obj) && obj.every(isOdometryPose);
}

/**
 * @returns the trajectory described by the file attached to an odometry path
 * @param path the odometry path to load the trajectory for
 * @param projectApi used to sign the url of the attached file
 */
async function loadOdometryPath(
  path: IElementOdometryPath,
  projectApi: ProjectApi,
): Promise<OdometryPose[]> {
  const signedUrl = await createSignedUrlForIElementUri(
    projectApi,
    path,
  ).requestSignedUrl();

  return await robustFetchJson({
    input: signedUrl,
    validateJson: isOdometryPoseArray,
  });
}

function getOrLoadEntry(
  path: IElementOdometryPath,
  projectApi: ProjectApi,
): OdometryPathEntry {
  const cached = CACHE.get(path.id);
  if (cached) return cached;

  const entry: OdometryPathEntry = {
    key: path.id,
    promise: loadOdometryPath(path, projectApi),
    listeners: new Set(),
  };
  entry.promise
    .then((poses) => {
      entry.poses = poses;
      for (const listener of entry.listeners) listener();
      scheduleEvictionIfUnused(entry);
    })
    .catch((error: unknown) => {
      // Dropping the entry lets a later render retry a transient failure
      CACHE.delete(entry.key);
      logError(error);
    });

  CACHE.set(entry.key, entry);
  return entry;
}

/**
 * @returns the trajectory of an odometry path, or undefined while it is being loaded
 * @param path the odometry path to read the trajectory of
 */
export function useOdometryPathPoses(
  path: IElementOdometryPath | undefined,
): OdometryPose[] | undefined {
  const { projectApiClient } = useApiClientContext();

  const entry = path ? getOrLoadEntry(path, projectApiClient) : undefined;

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!entry) return () => {};

      window.clearTimeout(entry.disposeTimeout);
      entry.disposeTimeout = undefined;
      entry.listeners.add(onStoreChange);

      return () => {
        entry.listeners.delete(onStoreChange);
        scheduleEvictionIfUnused(entry);
      };
    },
    [entry],
  );

  const getSnapshot = useCallback(() => entry?.poses, [entry]);

  // A load resolving before the subscription notifies no listener; React catches it
  // by re-reading getSnapshot right after subscribing.
  return useSyncExternalStore(subscribe, getSnapshot);
}
